import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useUserProfile } from '@/contexts/user.context';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/campaign/page-header';
import { UserRole } from '@/lib/types';
import { KeywordsDetectorDashboard } from '@/components/tasks/KeywordsDetectorDashboard';
import { TCPADetectorDashboard } from '@/components/tasks/TCPADetectorDashboard';
import { BudgetDetectorDashboard } from '@/components/tasks/BudgetDetectorDashboard';
import { NegativeKeywordsManager } from '@/components/tasks/NegativeKeywordsManager';
import { AdminNotificationsDashboard } from '@/components/admin/AdminNotificationsDashboard';
import { Check, ChevronDown, Bell } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useAdminCampaignsQuery,
  useCreateRecommendationMutation,
  useProcessRecommendation,
} from '@/lib/api-extensions/admin-queries';
import { Input } from '@/components/ui/input';
import { LocationOpportunityCard } from '@/components/tasks/LocationOpportunityCard';
import { KeywordMetricsBackfillCard } from '@/components/campaign/keyword-metrics-backfill-card';




export const detectors = [
  { value: "analyze_negative_keywords", label: "Negative Keyword Analysis" },
  { value: "exact_match_keywords", label: "Exact Match Keywords" },
  { value: "generate_optimized_keywords", label: "Generate New Keywords" },
  { value: "no_conversion_keywords", label: "No Conversion Keywords" },
  { value: "tcpa_period", label: "TCPA Period Keywords" },
  { value: "zero_impression", label: "Zero Impression Keywords" },
  { value: "high_cpc_keywords", label: "High CPC Keywords"},
  { value: "brand_campaign_budget", label: "Brand Campaign Budget" },
  { value: "detect_alternate_locations", label: "Alternate Location Suggestions" }
];

// Define API return types
interface RecommendationResponse {
  success: boolean;
  message: string;
  recommendation_ids: number[];
  details?: {
    keyword_count?: number;
    action?: string;
    [key: string]: any;
  };
  job_name?: string;
}

export const Route = createFileRoute('/_authenticated/_layout/admin/')({
  component: AdminPage,
});

function AdminPage() {
  const { user } = useUserProfile();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Remove location recommendations query since we're using the new component

  /*
  const [algorithmSelector, setAlgorithmSelector] = useState<JSX.Element | null>(null);
  
  // Memoize the algorithm selector callback to prevent infinite renders
  const handleAlgorithmSelector = useCallback((selector: JSX.Element) => {
    setAlgorithmSelector(selector);
  }, []);
  */


  // State for form inputs
  const [selectedCampaign, setSelectedCampaign] = useState('');

  // State for recommendation tab
  const [recommendationType] = useState('underperforming_keywords');
  const [options] = useState({
    min_cost: 5,
    max_conv_rate: 0.01,
    min_impressions: 100,
    min_days: 14,
    max_keywords: 10,
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // State for task generation tab
  const [actionType, setActionType] = useState<string>();

  // Define taskReason state - we don't change it for now, so we don't need the setter
  const taskReason = 'zero_impressions';


  // Reset negative keywords analysis when campaign or action type changes
  useEffect(() => {
    setShowKeywordsDashboard(false);
    setShowNegativeKeywordsAnalysis(false);
  }, [selectedCampaign, actionType]);
  const [isGeneratingTask, setIsGeneratingTask] = useState(false);
  const [taskResponse, setTaskResponse] = useState<RecommendationResponse | null>(null);

  // Get all campaigns
  const { data: allCampaigns = [], isLoading: isCampaignsLoading } = useAdminCampaignsQuery();

  // Filter for live and paused campaigns
  const filteredCampaigns = useMemo(() => {
    return allCampaigns.filter((campaign) => {
      const state = campaign.state?.toLowerCase?.() || '';
      return state === 'live' || state === 'paused';
    });
  }, [allCampaigns]);

  const createRecommendationMutation = useCreateRecommendationMutation();
  const processRecommendationMutation = useProcessRecommendation(selectedCampaign);
  
  // Create a ref to track initial render
  const isInitialRender = useRef(true);

  // Add state for threshold
  const [threshold, setThreshold] = useState(3.0);
  const [thresholdApplied, setThresholdApplied] = useState(false);

  // Guard clause - only render for admin users
  if (!user || user.role !== UserRole.ADMIN) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 space-y-8 py-4">
          <Alert variant="destructive">
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You need admin privileges to access this page.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Generate recommendation
  const handleGenerate = async (processImmediately = false) => {
    if (!selectedCampaign) {
      toast({
        title: 'Error',
        description: 'Please select a campaign',
        variant: 'destructive',
      });
      return;
    }

    try {
      let result;

      if (processImmediately) {
        // Use the new process recommendation hook directly
        // This avoids creating a new recommendation first
        result = await processRecommendationMutation.mutateAsync({
          recommendation_type: recommendationType,
          options,
        });
      } else {
        // Just create the recommendation without processing
        result = await createRecommendationMutation.mutateAsync({
          campaignId: selectedCampaign,
          recommendationType,
          options,
        });
      }

      // Show success toast
      toast({
        title: 'Success',
        description: result.message || 'Recommendation created successfully',
      });

      // Invalidate relevant queries to refresh data
      if (processImmediately) {
        queryClient.invalidateQueries({
          queryKey: ['actionLogs', selectedCampaign],
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    }
  };

  // State for negative keywords analysis
  const [showNegativeKeywordsAnalysis, setShowNegativeKeywordsAnalysis] = useState(false);
  const [showKeywordsDashboard, setShowKeywordsDashboard] = useState(false);
  
  // State for admin sections
  const [activeSection, setActiveSection] = useState<'automations' | 'notifications'>('automations');
  
  // Generate task - wrapped in useCallback to avoid infinite loops with useEffect
  const handleGenerateTask = useCallback(async () => {
    
    if (!selectedCampaign) {
      toast({
        title: 'Error',
        description: 'Please select a campaign',
        variant: 'destructive',
      });
      return;
    }
    
    if (!actionType) {
      toast({
        title: 'Error',
        description: 'Please select an action type',
        variant: 'destructive',
      });
      return;
    }

    // Check if this is a negative keywords analysis action
    if (actionType === "analyze_negative_keywords") {
      setShowNegativeKeywordsAnalysis(true);
      return;
    }

    const detectorValues = detectors.map(d => d.value);

    if (detectorValues.includes(actionType) && actionType !== "analyze_negative_keywords") {
      setShowKeywordsDashboard(true);
      return;
    }

    setIsGeneratingTask(true);
    setTaskResponse(null);

    try {
      // For the first iteration, we're only supporting zero impression keywords
      const recommendationType = 'ZERO_IMPRESSION_KEYWORDS';
      const taskOptions = {
        action_type: actionType,
        reason: taskReason,
      };

      // Use the existing process recommendation mutation
      const result = await processRecommendationMutation.mutateAsync({
        recommendation_type: recommendationType,
        options: taskOptions,
      });

      setTaskResponse(result as RecommendationResponse);

      // Show success toast
      toast({
        title: 'Success',
        description: result.message || 'Task generated successfully',
      });

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ['actionLogs', selectedCampaign],
      });

      // Also invalidate recommendations queries to ensure UI is updated
      queryClient.invalidateQueries({
        queryKey: ['recommendations', selectedCampaign],
      });

      // Invalidate all recommendations to update any admin lists
      queryClient.invalidateQueries({
        queryKey: ['allRecommendations'],
      });
      
      // Invalidate the campaign tasks query
      queryClient.invalidateQueries({
        queryKey: ['campaignTasks', Number(selectedCampaign)],
      });
      
      // Invalidate all action logs queries but maintain filter state
      queryClient.invalidateQueries({
        queryKey: ['actionLogs'],
        refetchType: 'active', // Only refetch active queries, which will maintain current filters
      });
      
      // Invalidate task tracking
      queryClient.invalidateQueries({
        queryKey: ['campaignTaskTracking'],
      });
    } catch (error) {
      console.error('Task generation failed:', error);

      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });

      // Set error response for display
      setTaskResponse({
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        recommendation_ids: [],
      });
    } finally {
      setIsGeneratingTask(false);
      // Clear action type to prevent infinite loop
      setActionType(undefined);
    }
  }, [
    selectedCampaign,
    actionType,
    taskReason,
    processRecommendationMutation,
    queryClient,
    toast,
    setShowNegativeKeywordsAnalysis,
    setShowKeywordsDashboard,
    setIsGeneratingTask,
    setTaskResponse
  ]);
  
  // Auto-generate when both campaign and action type are selected (or when either changes)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    
    const shouldGenerate = selectedCampaign && actionType;
    const isReady = !isGeneratingTask && shouldGenerate;
    
    if (isReady) {
      // Auto-generate without explicit button click
      handleGenerateTask();
    }
  }, [selectedCampaign, actionType, isGeneratingTask, handleGenerateTask]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 space-y-8 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <PageHeader title="Glitch Admin" />
          
          {/* Section Tabs */}
          <div className="flex gap-2">
            <Button
              variant={activeSection === 'automations' ? 'default' : 'outline'}
              onClick={() => setActiveSection('automations')}
              className="flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Automations
            </Button>
            <Button
              variant={activeSection === 'notifications' ? 'default' : 'outline'}
              onClick={() => setActiveSection('notifications')}
              className="flex items-center gap-2"
            >
              <Bell className="h-4 w-4" />
              Notifications
            </Button>
          </div>
        </div>
        
        {/* Automations Section */}
        {activeSection === 'automations' && (
          <div className="space-y-6">
            {/* Location Opportunities */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Location Expansion Opportunities</h3>
              <LocationOpportunityCard />
            </div>
            
            {/* Campaign-Specific Tools */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Campaign-Specific Tools</h3>
              <div className="flex flex-col sm:flex-row gap-3 items-end sm:items-center">
              {/* Campaign Selector */}
              <div className="w-56">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between bg-white"
                    >
                      <span className="truncate">
                        {selectedCampaign 
                          ? allCampaigns.find((c) => c.id.toString() === selectedCampaign)?.name || 'Select Campaign'
                          : 'Select Campaign'}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-64 max-h-[300px] overflow-y-auto"
                    align="end"
                  >
                    {isCampaignsLoading ? (
                      <div className='flex items-center justify-center p-2'>
                        <Spinner />
                      </div>
                    ) : filteredCampaigns?.length === 0 ? (
                      <div className='p-2 text-sm text-slate-500 text-center'>
                        No active or paused campaigns found
                      </div>
                    ) : (
                      filteredCampaigns?.map((campaign) => (
                        <DropdownMenuItem
                          key={campaign.id}
                          onClick={() => setSelectedCampaign(campaign.id.toString())}
                        >
                          <div className="flex items-center w-full">
                            <div className="w-5 mr-2 flex-shrink-0">
                              {selectedCampaign === campaign.id.toString() && (
                                <Check className="h-4 w-4" />
                              )}
                            </div>
                            <span className="truncate">{campaign.name} {campaign.state && `(${campaign.state})`}</span>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {/* Action Type Selector */}
              <div className="w-56">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between bg-white"
                    >
                      <span className="truncate">
                        {actionType ? actionType : 'Select action'}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end">
                    {detectors.map(({ value, label }) => (
                      <DropdownMenuItem key={value} onClick={() => {
                        setActionType(value);
                        if (value === 'high_cpc_keywords') {
                          setThresholdApplied(false);
                        } else {
                          setShowKeywordsDashboard(true);
                        }
                      }}>
                        <div className="flex items-center w-full">
                          <div className="w-5 mr-2 flex-shrink-0">
                            {actionType === value && <Check className="h-4 w-4" />}
                          </div>
                          <span>{label}</span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            
            {/* Task Response Display */}
            {taskResponse && (
          <Alert className='mt-4' variant={taskResponse.success ? 'default' : 'destructive'}>
            <AlertTitle className='flex items-center'>
              {taskResponse.success ? (
                <>
                  <svg
                    className='h-4 w-4 mr-2'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                  </svg>
                  Optimization Generated Successfully!
                </>
              ) : (
                <>
                  <svg
                    className='h-4 w-4 mr-2'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                  Optimization Generation Failed
                </>
              )}
              {taskResponse.job_name && (
                <Badge variant='outline' className='ml-2'>
                  Processed
                </Badge>
              )}
            </AlertTitle>
            <AlertDescription>
              <div>{taskResponse.message}</div>

              {taskResponse.recommendation_ids && taskResponse.recommendation_ids.length > 0 && (
                <div className='mt-2'>
                  <span className='font-medium'>Recommendation IDs:</span>{' '}
                  {taskResponse.recommendation_ids.join(', ')}
                </div>
              )}

              {taskResponse.details && (
                <div className='mt-2 border-t pt-2'>
                  <h4 className='text-sm font-medium mb-1'>Task Details:</h4>
                  {Object.entries(taskResponse.details).map(([key, value]) => (
                    <div key={key} className='flex items-start text-sm py-1'>
                      <span className='font-medium min-w-32'>{key.replace(/_/g, ' ')}:</span>{' '}
                      <span className='ml-2'>{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                    </div>
                  ))}
                </div>
              )}

              {taskResponse.success && (
                <div className='mt-4 bg-muted p-3 rounded-md text-sm'>
                  <p>
                    The optimization has been generated and will be processed automatically. You can view the results in the
                    Tasks section.
                  </p>
                </div>
              )}
            </AlertDescription>
          </Alert>
            )}
            
            {/* Loading indicator while generating task */}
            {isGeneratingTask && (
              <div className="mt-4 py-8">
                <div className="flex flex-col items-center justify-center">
                  <Spinner className="h-8 w-8 mb-4" />
                  <p className="text-sm text-slate-500">Generating optimization...</p>
                </div>
              </div>
            )}
            
            {/* Separate Negative Keywords Analysis Panel */}
            {showNegativeKeywordsAnalysis && (
              (() => {
                // Find the selected campaign in our filtered list
                const campaign = filteredCampaigns.find(c => c.id.toString() === selectedCampaign);
                // If found, pass the slug and selected algorithm
                if (campaign) {
                  return <NegativeKeywordsManager campaignSlug={campaign.slug}/>;
                } else {
                  console.warn("Could not find campaign with ID:", selectedCampaign);
                  return <div className="p-4 text-amber-700 bg-amber-50 rounded-md">
                    Campaign data not available. Please reload the page and try again.
                  </div>;
                }
              })()
            )}

            {showKeywordsDashboard && actionType === 'high_cpc_keywords' && thresholdApplied && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm text-muted-foreground">Threshold: <b>{threshold}</b></span>
                  <Button size="sm" variant="outline" onClick={() => setThresholdApplied(false)}>
                    Change
                  </Button>
                </div>
                {(() => {
                  const campaign = filteredCampaigns.find(c => c.id.toString() === selectedCampaign);
                  const selectedDetector = detectors.find(d => d.value === actionType);
                  if (campaign) {
                    return <KeywordsDetectorDashboard campaignId={campaign.id} detector={actionType} detectorLabel={selectedDetector?.label || ""} threshold={threshold}/>;
                  }
                  return null;
                })()}
              </>
            )}

            {showKeywordsDashboard && actionType !== 'high_cpc_keywords' && actionType !== "analyze_negative_keywords" && (
              (() => {
                const campaign = filteredCampaigns.find(c => c.id.toString() === selectedCampaign);
                const selectedDetector = detectors.find(d => d.value === actionType);
                if (campaign && actionType) {
                  if (actionType === "tcpa_period") {
                    return <TCPADetectorDashboard campaignId={campaign.id} detector={actionType} detectorLabel={selectedDetector?.label || ""}/>;
                  } else if (actionType === "brand_campaign_budget") {
                    return <BudgetDetectorDashboard campaignId={campaign.id} detector={actionType} detectorLabel={selectedDetector?.label || ""}/>;
                  } else {
                    return <KeywordsDetectorDashboard campaignId={campaign.id} detector={actionType} detectorLabel={selectedDetector?.label || ""}/>;
                  }
                }
                return null;
              })()
            )}

            {/* Keyword Metrics Backfill - shown when campaign is selected */}
            {selectedCampaign && (
              <KeywordMetricsBackfillCard campaignId={Number(selectedCampaign)} />
            )}
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Process Recommendation Immediately?</AlertDialogTitle>
              <AlertDialogDescription>
                This will generate a recommendation and immediately apply it to the campaign. Any actions taken will
                affect your Google Ads account. This operation cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setShowConfirmDialog(false);
                  handleGenerate(true);
                }}
              >
                Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        
        {/* Notifications Section */}
        {activeSection === 'notifications' && (
          <div className="space-y-6">
            <AdminNotificationsDashboard />
          </div>
        )}

        {actionType === 'high_cpc_keywords' && selectedCampaign && !thresholdApplied && (
          <div className="flex flex-col gap-2 mb-4 max-w-xs">
            <label htmlFor="high-cpc-threshold" className="block text-sm font-medium">High CPC Threshold (multiplier, e.g. 3.0)</label>
            <div className="flex gap-2 items-center">
              <Input
                id="high-cpc-threshold"
                type="number"
                min={0.1}
                step={0.1}
                value={threshold}
                onChange={e => {
                  setThreshold(Number(e.target.value));
                  setThresholdApplied(false);
                }}
                className="w-24"
              />
              <Button
                size="sm"
                onClick={() => {
                  setShowKeywordsDashboard(true);
                  setThresholdApplied(true);
                }}
                disabled={thresholdApplied || !threshold || threshold <= 0}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}